import { useTu } from "@src/appV2/i18n/useTu";

/* eslint-disable import/max-dependencies -- Chat actions compose multiple features */
import { useSession } from "@src/appV2/Auth/api/useSession";
import { useShowInHomeLockToggle } from "@src/appV2/ExperimentalHomeHealth/lib/useShowInHomeLockToggle";
import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useToast } from "@src/appV2/lib/Notifications/Toasts/useToast";
import { useDeleteStarredProfessional } from "@src/appV2/Professionals/api/starredProfessionals/useDeleteStarredProfessional";
import { usePostStarredProfessional } from "@src/appV2/Professionals/api/starredProfessionals/usePostStarredProfessional";
import { useWorkplaceStarredProfessionals } from "@src/appV2/Professionals/api/starredProfessionals/useWorkplaceStarredProfessionals";
import { useShiftManagementPermission } from "@src/appV2/redesign/DailyView/hooks/useShiftManagementPermission";
import { type AgentProfile } from "@src/appV2/Worker/types";
import { WorkerRequestedCancelDialog } from "@src/appV2/WorkerRequestedCancel/WorkerRequestedCancelDialog";
import { useModalState } from "@src/lib/ui-react";
import { useCallback } from "react";

import { type ButtonSize } from "../../../components/Button";
import { ProfessionalMoreMenu } from "../../../MyProfessionals/components/ProfessionalMoreMenu";
import { useWorkerBlockedStatus } from "../../api/useWorkerBlockedStatus";
import { useGetWorkerLockStatus } from "../../InHomeLock/api/useGetWorkerLockStatus";
import { LockedByOtherDialog } from "../../InHomeLock/components/LockedByOtherDialog";
import { LockFlowDialog } from "../../InHomeLock/components/LockFlowDialog";
import { UnlockConfirmationDialog } from "../../InHomeLock/components/UnlockConfirmationDialog";
import { type ChatLockStatus, getChatActionMenuItems } from "./getChatActionMenuItems";

/* eslint-enable import/max-dependencies */

interface ChatActionsMenuProps {
  /** Worker profile from useWorker. Used for worker-requested-cancel context. */
  worker?: AgentProfile;
  workerId: string;
  workplaceId: string;
  workerName?: string;
  /** Trigger button size. Match the sibling header action buttons. */
  size?: ButtonSize;
  /** When provided, adds a "Mark conversation as unread" item. */
  onMarkConversationAsUnread?: () => void;
}

const STARRED_PROFESSIONAL_EVENTS = {
  starred: "CHAT_PAGE_STARRED_PROFESSIONAL",
  unstarred: "CHAT_PAGE_REMOVED_STARRED_PROFESSIONAL",
} as const;

/**
 * Shared kebab menu for chat worker actions, used by both the panel header
 * (`ChatHeader`) and the full-page conversation header (`ChatConversationHeader`).
 *
 * Consolidates Star/Unstar, "Worker asked me to cancel", Lock/Unlock/Locked, and
 * "Mark conversation as unread" into a single data-driven menu, mirroring the
 * `ProfessionalMoreMenu` / `getProfessionalMoreMenuItems` pattern. Worker sessions
 * naturally produce an empty item set, so the menu renders nothing for them.
 */
export function ChatActionsMenu(props: ChatActionsMenuProps) {
  const { tu } = useTu("chat");
  const { showErrorToast } = useToast();

  const { worker, workerId, workplaceId, workerName, size, onMarkConversationAsUnread } = props;

  const session = useSession();
  const isAdmin = Boolean(session.admin);
  const { canCancelShifts } = useShiftManagementPermission();

  // In-Home Worker Lock
  const showLockToggle = useShowInHomeLockToggle();
  const { isLocked, lockedByCurrentWorkplace, lockData } = useGetWorkerLockStatus({
    workerId,
    workplaceId,
    enabled: showLockToggle,
  });

  // Star professional
  const { isStarredProfessionalEnabled, starredProfessionalId } = useWorkplaceStarredProfessionals({
    workplaceId,
    workerId,
  });

  const isStarred = Boolean(starredProfessionalId);

  const { mutate: starProfessional, isLoading: isStarring } = usePostStarredProfessional({
    workplaceId,
    professionalId: workerId,
  });

  const { mutate: unstarProfessional, isLoading: isUnstarring } = useDeleteStarredProfessional({
    workplaceId,
    starredProfessionalId,
  });

  // Worker requested cancel
  const isWorkerRequestedCancelAlertEnabled = useCbhFlag(
    CbhFeatureFlag.WORKER_REQUESTED_CANCEL_ALERT,
    { defaultValue: false }
  );

  const workerCancelModalState = useModalState();
  const lockFlowModalState = useModalState();
  const unlockModalState = useModalState();
  const lockedByOtherModalState = useModalState();

  const { data: blockedStatus } = useWorkerBlockedStatus({ workplaceId, workerId });
  const isBlocked = blockedStatus?.isBlocked ?? false;

  const handleToggleStar = useCallback(() => {
    if (!isStarred && isBlocked) {
      logEvent(APP_V2_USER_EVENTS.STARRED_PROFESSIONAL_ATTEMPTED_BUT_BLOCKED, {
        workerId,
        workplaceId,
        source: "Chat",
      });
      showErrorToast(tu("Professionals on your DNR list cannot be added to your starred list"));
      return;
    }

    if (isStarred) {
      if (!starredProfessionalId) {
        return;
      }

      unstarProfessional(undefined, {
        onSuccess: () => {
          logEvent(STARRED_PROFESSIONAL_EVENTS.unstarred, {
            workerId,
            workplaceId,
            source: "Chat",
          });
        },
      });
    } else {
      starProfessional(undefined, {
        onSuccess: () => {
          logEvent(STARRED_PROFESSIONAL_EVENTS.starred, {
            workerId,
            workplaceId,
            source: "Chat",
          });
        },
      });
    }
  }, [
    isBlocked,
    isStarred,
    showErrorToast,
    starProfessional,
    starredProfessionalId,
    tu,
    unstarProfessional,
    workerId,
    workplaceId,
  ]);

  const lockStatus: ChatLockStatus = isLocked
    ? lockedByCurrentWorkplace
      ? "lockedByCurrentWorkplace"
      : "lockedByOther"
    : "unlocked";

  const handleLock = useCallback(() => {
    if (!isLocked) {
      lockFlowModalState.openModal();
    } else if (lockedByCurrentWorkplace) {
      unlockModalState.openModal();
    } else {
      lockedByOtherModalState.openModal();
    }
  }, [
    isLocked,
    lockedByCurrentWorkplace,
    lockFlowModalState,
    unlockModalState,
    lockedByOtherModalState,
  ]);

  const items = getChatActionMenuItems({
    isStarred,
    isStarToggling: isStarring || isUnstarring,
    showStar: isStarredProfessionalEnabled && !isAdmin && !(isBlocked && !isStarred),
    showCancel: isWorkerRequestedCancelAlertEnabled && canCancelShifts,
    showMarkUnread: Boolean(onMarkConversationAsUnread),
    showLock: showLockToggle,
    lockStatus,
    onToggleStar: handleToggleStar,
    onCancel: workerCancelModalState.openModal,
    onMarkConversationAsUnread: onMarkConversationAsUnread ?? (() => {}),
    onLock: handleLock,
    tu,
  });

  return (
    <>
      <ProfessionalMoreMenu
        items={items}
        triggerIconType="ellipsis-vertical"
        triggerButtonProps={{ size, circular: true }}
      />
      <WorkerRequestedCancelDialog
        modalState={workerCancelModalState}
        workerId={workerId}
        workerName={workerName}
        workerAvatarUrl={worker?.profileImageUrl ?? undefined}
        workerQualification={worker?.qualification ?? undefined}
        facilityId={workplaceId}
      />
      <LockFlowDialog
        modalState={lockFlowModalState}
        workerId={workerId}
        workplaceId={workplaceId}
      />
      <UnlockConfirmationDialog
        modalState={unlockModalState}
        lockReason={lockData?.attributes.lockReason ?? ""}
        unlockCondition={lockData?.attributes.unlockCondition ?? ""}
        lockId={lockData?.id ?? ""}
        workplaceId={workplaceId}
      />
      <LockedByOtherDialog modalState={lockedByOtherModalState} />
    </>
  );
}
